import type { SelectedProductGroup } from "@/features/Generations/context/PilotStateContext";
import type { ProductCardsTaxonomyFilter } from "@/types/ingestedProduct";

import { propagationGroupHandle } from "@/features/Generations/propagationList";
import {
  PRODUCT_GROUP_TYPE,
  type Propagation,
  type ProductGroupType,
} from "@/types/propagation";

export type PilotFlowGroupType = "collection" | "category" | "tag";

/** Maps the URL-facing group type to the proto `ProductGroupType` enum. */
export const PRODUCT_GROUP_TYPE_BY_GROUP = {
  collection: PRODUCT_GROUP_TYPE.COLLECTION,
  category: PRODUCT_GROUP_TYPE.CATEGORY,
  tag: PRODUCT_GROUP_TYPE.TAG,
} as const satisfies Record<PilotFlowGroupType, ProductGroupType>;

export function isPilotFlowGroupType(type: string): type is PilotFlowGroupType {
  return Object.hasOwn(PRODUCT_GROUP_TYPE_BY_GROUP, type);
}

/** Handle → `{ collection | category | tag }` filter used by product-cards. */
export function taxonomyFilterForGroup(
  groupType: PilotFlowGroupType,
  handle: string,
): ProductCardsTaxonomyFilter {
  return { [groupType]: handle };
}

export function pilotFlowGroupTypeForProto(
  proto: string,
): PilotFlowGroupType | undefined {
  const match = (
    Object.entries(PRODUCT_GROUP_TYPE_BY_GROUP) as [
      PilotFlowGroupType,
      ProductGroupType,
    ][]
  ).find(([, value]) => value === proto);
  return match?.[0];
}

export type PilotFlowGroupParams = {
  groupName?: string;
  groupId?: string;
  groupType?: PilotFlowGroupType;
  allProducts?: boolean;
};

/**
 * URL group params for reopening an existing propagation: its handle comes
 * from the stored name and its type from `group_type`. An All Products run
 * (UNSPECIFIED type) maps to `allProducts` with the display name kept.
 */
export function pilotFlowGroupParamsForPropagation(
  propagation: Propagation,
): PilotFlowGroupParams {
  const groupName = propagationGroupHandle(propagation);
  const proto = propagation.group_type;
  if (!proto) return groupName ? { groupName } : {};
  if (proto === PRODUCT_GROUP_TYPE.UNSPECIFIED) {
    return { allProducts: true, ...(groupName ? { groupName } : {}) };
  }
  const groupType = pilotFlowGroupTypeForProto(proto);
  if (!groupType) return groupName ? { groupName } : {};
  const groupId = propagation.group_id?.trim();
  return {
    groupType,
    ...(groupName ? { groupName } : {}),
    ...(groupId ? { groupId } : {}),
  };
}

export function productCardsFilterForPilotGroup({
  groupName,
  groupType,
  allProducts,
}: PilotFlowGroupParams): ProductCardsTaxonomyFilter | undefined {
  // The product-cards endpoint filters by handle, which travels as `groupName`
  // (`groupId` carries the catalog numeric id and is not a valid filter value).
  if (groupName && groupType)
    return taxonomyFilterForGroup(groupType, groupName);
  if (allProducts) return {};
  return undefined;
}

/**
 * An All Products launch targets no taxonomy, so it carries no id or handle —
 * the explicit product list on the propagate request is its scope.
 */
export const ALL_PRODUCTS_GROUP: SelectedProductGroup = {
  group_type: PRODUCT_GROUP_TYPE.UNSPECIFIED,
};

export function productGroupForPilotGroup({
  groupId,
  groupName,
  groupType,
  allProducts,
}: PilotFlowGroupParams): SelectedProductGroup | undefined {
  // A handle (`groupName`) is enough to identify and filter a group; the numeric
  // `groupId` is absent for search-picked and deep-linked taxonomies, so don't
  // require it. Both fields are omitted when absent rather than set undefined.
  if (groupType && (groupName || groupId)) {
    return {
      group_type: PRODUCT_GROUP_TYPE_BY_GROUP[groupType],
      ...(groupId ? { group_id: groupId } : {}),
      ...(groupName ? { handle: groupName } : {}),
    };
  }
  return allProducts ? ALL_PRODUCTS_GROUP : undefined;
}

export function productCardsFilterForSelectedGroup(
  group: SelectedProductGroup,
): ProductCardsTaxonomyFilter | undefined {
  // Empty but present: list the whole catalog rather than nothing.
  if (group.group_type === PRODUCT_GROUP_TYPE.UNSPECIFIED) return {};
  const handle = group.handle || group.group_id;
  if (!handle) return undefined;
  const groupType = pilotFlowGroupTypeForProto(group.group_type);
  return groupType ? taxonomyFilterForGroup(groupType, handle) : undefined;
}

/** Search params of the SKU Store landing page (`/sku-store/$siteId`). */
export type SkuStoreLandingSearch = {
  collection?: string;
  category?: string;
  tag?: string;
  tab?: "products" | "media";
};

/**
 * Deep link to a group's Media tab on the SKU Store landing page. Without a
 * resolvable group (e.g. an All Products run) it opens All Products' media.
 */
export function skuStoreMediaSearchForGroup(
  groupType?: string,
  groupName?: string,
): SkuStoreLandingSearch {
  const handle = groupName?.trim();
  if (groupType && handle && isPilotFlowGroupType(groupType)) {
    return { [groupType]: handle, tab: "media" };
  }
  return { tab: "media" };
}
